// Style costing sheet and supplier quote comparison (garment → Costing & quotes). Every line is quantity × unit price
// in its own currency, converted to the organisation's base currency with the exchange rates in Settings (the rates
// and their date are kept with each costing version).
export const COST_SECTIONS = [
  ["fabric_main", "Main fabric"], ["fabric_other", "Other fabrics"], ["trims", "Trims"], ["labels", "Labels & packaging"],
  ["cmt", "CMT / labour"], ["testing", "Testing & certification"], ["overheads", "Overheads"], ["factory_margin", "Factory margin"],
] as const;
export type CostSection = (typeof COST_SECTIONS)[number][0];
export const COST_SECTION_LABEL = Object.fromEntries(COST_SECTIONS) as Record<CostSection, string>;
export const COST_CURRENCIES = ["USD", "GBP", "EUR", "SAR", "AED", "PKR", "CNY", "BDT", "INR", "TRY"] as const;

export interface CostLine { id: string; section: CostSection; description: string; qty: number; unit: string; unitPrice: number; currency: string }
export interface Rates { rates: Record<string, number>; date: string }   // 1 unit of a currency = n base currency
const r4 = (n: number) => Math.round(n * 10000) / 10000;
const r2 = (n: number) => Math.round(n * 100) / 100;

export function normalizeRates(v: any, base: string): Rates {
  const rates: Record<string, number> = { [base]: 1 };
  for (const [k, x] of Object.entries(v?.rates || {})) if (/^[A-Z]{3}$/.test(k) && k !== base && Number(x) > 0) rates[k] = Math.round(Number(x) * 1e6) / 1e6;
  return { rates, date: /^\d{4}-\d{2}-\d{2}$/.test(String(v?.date || "")) ? String(v.date) : "" };
}
/** An amount in the base currency (null when there is no rate for its currency). */
export const toBase = (amount: number, currency: string, r: Rates): number | null => (r.rates[currency] ? r4(amount * r.rates[currency]) : null);

let seq = 0;
export function normalizeCostLines(v: unknown, base: string): CostLine[] {
  return (Array.isArray(v) ? v : []).slice(0, 80).map((x: any) => ({
    id: String(x?.id || `l${Date.now().toString(36)}${(seq++).toString(36)}`).slice(0, 40),
    section: (COST_SECTIONS.some(([k]) => k === x?.section) ? x.section : "overheads") as CostSection,
    description: String(x?.description || "").trim().slice(0, 200),
    qty: Math.max(0, r4(Number(x?.qty) || 0)), unit: String(x?.unit || "").slice(0, 20),
    unitPrice: Math.max(0, r4(Number(x?.unitPrice) || 0)),
    currency: (COST_CURRENCIES as readonly string[]).includes(x?.currency) ? x.currency : base,
  }));
}

export interface CostingInput { lines: CostLine[]; agentCommission: number; targetFob: number | null; targetRetail: number | null; targetMargin: number | null; freightPerUnit: number; dutyPct: number }
/** The totals: by section, FOB (sum of the lines), agent commission on top, and the comparison with the targets. */
export function computeStyleCosting(c: CostingInput, r: Rates) {
  const bySection: Record<string, number> = {};
  const lines = c.lines.map((l) => { const total = toBase(l.qty * l.unitPrice, l.currency, r); if (total !== null) bySection[l.section] = r4((bySection[l.section] || 0) + total); return { ...l, total: r4(l.qty * l.unitPrice), base: total }; });
  const missingRates = [...new Set(lines.filter((l) => l.base === null).map((l) => l.currency))];
  const fob = r4(Object.values(bySection).reduce((a, b) => a + b, 0));
  const commission = r4(fob * (c.agentCommission || 0) / 100);
  const cost = r4(fob + commission);
  return {
    lines, bySection, fob, commission, cost, missingRates,
    vsTarget: c.targetFob ? { diff: r4(fob - c.targetFob), pct: r2(((fob - c.targetFob) / c.targetFob) * 100) } : null,
    margin: marginOf(cost, c.freightPerUnit, c.dutyPct, c.targetRetail),
  };
}
/** Landed cost (FOB + freight + duty) and the margin against the sell price. */
export function marginOf(fob: number, freightPerUnit: number, dutyPct: number, retail: number | null, upliftPct: number | null = null) {
  const landed = upliftPct !== null ? r4(fob * (1 + upliftPct / 100)) : r4(fob * (1 + (dutyPct || 0) / 100) + (freightPerUnit || 0));
  return { landed, margin: retail ? r4(retail - landed) : null, marginPct: retail ? r2(((retail - landed) / retail) * 100) : null };
}

export interface QuoteRow { id: string; fob: number; currency: string; leadTime: number | null; validUntil: string | null }
/** Each quote against the target, with the cheapest and fastest still-valid quotes marked. */
export function compareQuotes<T extends QuoteRow>(quotes: T[], r: Rates, targetFob: number | null, today: string) {
  const rows = quotes.map((q) => {
    const base = toBase(q.fob, q.currency, r);
    const expired = !!q.validUntil && q.validUntil < today;
    return { ...q, base, expired, vsTarget: base !== null && targetFob ? r2(((base - targetFob) / targetFob) * 100) : null };
  });
  const valid = rows.filter((q) => !q.expired && q.base !== null);
  const cheapest = valid.length ? valid.reduce((a, b) => (b.base! < a.base! ? b : a)).id : null;
  const timed = valid.filter((q) => q.leadTime !== null && q.leadTime !== undefined);
  const fastest = timed.length ? timed.reduce((a, b) => (b.leadTime! < a.leadTime! ? b : a)).id : null;
  return rows.map((q) => ({ ...q, cheapest: q.id === cheapest, fastest: q.id === fastest }));
}
